import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { HelpCircleIcon, SaveIcon, TrashIcon, UndoIcon } from "lucide-react";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import proceduresLegend from "@/assets/procedure-images/procedures-legend.jpeg";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@/components/ui/dialog";
import { Separator } from "@/components/ui/separator";
import { parsePocketBaseError } from "@/lib/errors/pocketbase";
import { Odontogram } from "@/modules/patients/odontograms/Odontogram";
import { useOdontogramActions } from "@/modules/patients/odontograms/odontogramStore";
import { ProcedureSelector } from "@/modules/patients/odontograms/ProcedureSelector";
import {
	createOdontogram,
	isDuplicatePatientOdontogramError,
	odontogramQueries,
	updateOdontogram,
} from "@/modules/patients/odontograms/queries";

export const Route = createFileRoute("/dashboard/patients/$patientId/odontogram")({
	component: RouteComponent,
});

function showOdontogramErrorToast(error: unknown, opts: { id: string; title: string; fallbackDescription: string }) {
	const parsed = parsePocketBaseError(error, {});

	toast.error(opts.title, {
		id: opts.id,
		description: parsed.message || opts.fallbackDescription,
	});
}

function RouteComponent() {
	const { patientId } = useParams({ from: "/dashboard/patients/$patientId/odontogram" });

	const odontogramQuery = useQuery(odontogramQueries.one({ patientId: patientId }));

	const { selectProcedure, setTeeth } = useOdontogramActions();

	useEffect(() => {
		selectProcedure(1);

		if (odontogramQuery.data?.json?.teeth) {
			setTeeth(odontogramQuery.data?.json?.teeth);
		}

		return () => {
			setTeeth([]);
		};
	}, [odontogramQuery.data?.json?.teeth, setTeeth, selectProcedure, odontogramQuery.data?.json]);

	return (
		<div>
			<OdontogramHeader odontogram={odontogramQuery.data} />
			<Card>
				<CardContent>
					<div className="@container">
						<Odontogram />

						<Separator className="my-6" />

						<div className="flex flex-col items-center justify-between gap-4">
							<ProcedureSelector />
						</div>
					</div>
				</CardContent>
			</Card>
		</div>
	);
}

const OdontogramHeader = ({ odontogram }: { odontogram?: { id: string } }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const { patientId } = useParams({ from: "/dashboard/patients/$patientId/odontogram" });

	const { undo, clear, getTeeth } = useOdontogramActions();

	const { mutate: create } = useMutation({
		mutationFn: () => {
			toast.loading(
				t(($) => $.modules.patients.odontogram.create.status.loading.title),
				{
					id: "create-odontogram",
					description: t(($) => $.modules.patients.odontogram.create.status.loading.message),
				}
			);

			return createOdontogram({ patientId, teeth: getTeeth() });
		},
		onSuccess: () => {
			toast.success(
				t(($) => $.modules.patients.odontogram.create.status.success.title),
				{
					id: "create-odontogram",
					description: t(($) => $.modules.patients.odontogram.create.status.success.message),
				}
			);

			queryClient.invalidateQueries({
				queryKey: odontogramQueries.one({ patientId: patientId }).queryKey,
			});
		},
		onError: (error) => {
			if (isDuplicatePatientOdontogramError(error)) {
				// Another session already created this patient's odontogram between our initial
				// render and this save; refetch so the next save updates it instead of repeating
				// the same failed create.
				queryClient.invalidateQueries({
					queryKey: odontogramQueries.one({ patientId: patientId }).queryKey,
				});

				toast.error(
					t(($) => $.modules.patients.odontogram.create.status.duplicate.title),
					{
						id: "create-odontogram",
						description: t(($) => $.modules.patients.odontogram.create.status.duplicate.message),
					}
				);
				return;
			}

			showOdontogramErrorToast(error, {
				id: "create-odontogram",
				title: t(($) => $.modules.patients.odontogram.create.status.error.title),
				fallbackDescription: t(($) => $.modules.patients.odontogram.create.status.error.message),
			});
		},
	});

	const { mutate: update } = useMutation({
		mutationFn: ({ id }: { id: string }) => {
			toast.loading(
				t(($) => $.modules.patients.odontogram.update.status.loading.title),
				{
					id: "update-odontogram",
					description: t(($) => $.modules.patients.odontogram.update.status.loading.message),
				}
			);

			return updateOdontogram({ id, teeth: getTeeth() });
		},
		onSuccess: () => {
			toast.success(
				t(($) => $.modules.patients.odontogram.update.status.success.title),
				{
					id: "update-odontogram",
					description: t(($) => $.modules.patients.odontogram.update.status.success.message),
				}
			);

			queryClient.invalidateQueries({
				queryKey: odontogramQueries.one({ patientId: patientId }).queryKey,
			});
		},
		onError: (error) => {
			showOdontogramErrorToast(error, {
				id: "update-odontogram",
				title: t(($) => $.modules.patients.odontogram.update.status.error.title),
				fallbackDescription: t(($) => $.modules.patients.odontogram.update.status.error.message),
			});
		},
	});

	const onSave = useCallback(
		({ odontogramId }: { odontogramId?: string }) => {
			if (odontogramId) {
				update({ id: odontogramId });
				return;
			}

			create();
		},
		[create, update]
	);

	const onUndo = useCallback(() => undo(), [undo]);
	const onClear = useCallback(() => clear(), [clear]);

	return (
		<div className="mt-8 mb-2 flex flex-col items-center justify-between gap-4 md:flex-row">
			<div className="flex w-full flex-wrap justify-end gap-2">
				<HelpButton />

				<Button onClick={onUndo} variant="outline">
					<UndoIcon className="size-4" />
					{t(($) => $.modules.patients.odontogram.buttons.undo)}
				</Button>
				<Button onClick={onClear} variant="outline">
					<TrashIcon className="size-4" />
					{t(($) => $.modules.patients.odontogram.buttons.clear)}
				</Button>
				<Button onClick={() => onSave({ odontogramId: odontogram?.id })}>
					<SaveIcon className="size-4" />
					{t(($) => $.modules.patients.odontogram.buttons.save)}
				</Button>
			</div>
		</div>
	);
};

const HelpButton = () => {
	const { t } = useTranslation();

	return (
		<Dialog>
			<DialogTrigger asChild>
				<Button className="mb-2" size={"icon"} variant={"ghost"}>
					<HelpCircleIcon className="size-6" />
				</Button>
			</DialogTrigger>
			<DialogContent>
				<DialogHeader>
					<DialogTitle>{t(($) => $.modules.patients.odontogram.legend.title)}</DialogTitle>
					<DialogDescription>
						<img alt="procedure's legend" height={600} src={proceduresLegend} width={600} />
					</DialogDescription>
				</DialogHeader>
			</DialogContent>
		</Dialog>
	);
};
